import React, {ReactElement, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Button,
  ShareIcon,
  FastIcon,
  CartIcon,
  DialogLegacy as Dialog,
  DialogLegacyContent as DialogContent,
  DialogLegacyActions as DialogActions,
  Alert,
  AlertTitle,
  Typography,
  Box,
  Stack,
  AlertWarningIcon
} from '@customink/pigment-react';
import {
  CampaignAttributes,
  ShippingType,
  StoreDetails
} from '../../models/storeDetails';
import services from '../../api/remoteServices';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import {
  useGetStoreAddressesQuery,
  useGetStoreDetailsQuery
} from '../../api/storesApi';
import CopyLinkTextButton from '../CopyLinkTextButton';
import {
  useStoreSlug,
  useGroupOrderTimeline,
  useAppSelector,
  useFtux
} from '../../hooks';
import './index.scss';

type DialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  storeSlug: string;
  storeDetails: StoreDetails;
  primaryCampaign: CampaignAttributes;
};

const GroupOrderDialog: React.FC<DialogProps> = ({
  isOpen,
  handleClose,
  storeSlug,
  storeDetails,
  primaryCampaign
}: DialogProps) => {
  const {closeDateFormatted} = useGroupOrderTimeline();

  const storeUrl = `${services.storefront}/${storeSlug}`;
  const noHttpStoreUrl = storeUrl.replace(/^https?:\/\//, '');

  const INFOGRAPHICS = [
    {
      key: 'share',
      icon: <ShareIcon />,
      title: 'Share Your Link',
      body: 'Share, share, share to make sure you meet your goal and get everyone their gear.'
    },
    {
      key: 'manage',
      icon: <FastIcon />,
      title: 'Manage Orders',
      body: 'Visit your dashboard to track progress, update settings, and review order details.'
    },
    {
      key: 'checkout',
      icon: <CartIcon />,
      title: 'Check Out',
      body: `When you're ready, complete your Group Order and get your gear going.`
    }
  ];

  const renderInfographics = () => {
    return (
      <div className="share-infographics">
        {INFOGRAPHICS.map(graphic => {
          return (
            <div className="infographic" key={graphic.key}>
              {graphic.icon}
              <Typography variant="bodyShort2" className="bolder">
                {graphic.title}
              </Typography>
              <Typography variant="bodyShort1">{graphic.body}</Typography>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      title="Your Group Order is Ready"
      open={isOpen}
      className="groupShare-dialog"
      maxWidth="md"
      onClose={handleClose}>
      <DialogContent>
        <Alert severity="info" className="info-alert" icon={false}>
          <AlertTitle>{storeDetails.name}</AlertTitle>
          <Typography variant="bodyLong1" className="info-alert-quantity">
            Expected Quantity: {primaryCampaign?.expectedQuantity}
          </Typography>
          <Typography variant="bodyLong1">
            Close Date: {closeDateFormatted}
          </Typography>
        </Alert>
        <div className="nextSteps">
          <Typography variant="subHeading" className="nextSteps-header bolder">
            What&apos;s Next?
          </Typography>
          {renderInfographics()}
        </div>
      </DialogContent>
      <DialogActions className="groupShare-dialog-actions">
        <div className="share-link">
          <Typography variant="bodyLong2">Share your link:</Typography>
          <Typography variant="bodyLong2" className="bolder">
            {noHttpStoreUrl}
          </Typography>
        </div>
        <CopyLinkTextButton copyLink={storeUrl} size="medium" fullWidth />
        <Button fullWidth onClick={handleClose}>
          Manage Your Group Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const HybridOrderDialog: React.FC<DialogProps> = ({
  isOpen,
  handleClose,
  storeSlug,
  storeDetails,
  primaryCampaign
}: DialogProps) => {
  const {closeDateFormatted} = useGroupOrderTimeline();

  const storeSettingsUrl = `${services.storefront}/${storeSlug}/manage/settings?tab=1#shipping-details`;

  return (
    <Dialog
      title="Welcome to your Group Order"
      open={isOpen}
      className="groupShare-dialog"
      maxWidth="md"
      onClose={handleClose}>
      <DialogContent>
        <Alert severity="info" className="info-alert" icon={false}>
          <AlertTitle>{storeDetails.name}</AlertTitle>
          <Typography variant="bodyLong1" className="info-alert-quantity">
            Expected Quantity: {primaryCampaign?.expectedQuantity}
          </Typography>
          <Typography variant="bodyLong1">
            Close Date: {closeDateFormatted}
          </Typography>
        </Alert>
        <div className="nextSteps">
          <Typography variant="subHeading" className="nextSteps-header bolder">
            <AlertWarningIcon color="warning" fontSize="medium" />
            Enter Your Shipping Address
          </Typography>
          <Box>
            <Stack
              direction="column"
              spacing={2}
              sx={{textAlign: 'left', marginY: 1}}>
              <Typography className="nextSteps-hybrid-body">
                There&apos;s one more step to complete before you start
                collecting purchases. Go to your settings page and enter your
                shipping address so supporters may choose to ship to your
                location for free.
              </Typography>
            </Stack>
          </Box>
        </div>
      </DialogContent>
      <DialogActions className="groupShare-dialog-actions">
        <Button fullWidth href={storeSettingsUrl}>
          Enter Your Shipping Address
        </Button>
        <Button fullWidth variant="secondary" onClick={handleClose}>
          Manage Your Group Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GroupFtuxDialog: React.FC = (): ReactElement => {
  const urlSlug = useStoreSlug();
  useGetStoreDetailsQuery(urlSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const {data: storeAddressesData = [], isLoading: isAddressesLoading} =
    useGetStoreAddressesQuery({
      storeSlug: urlSlug
    });

  const showHybridMessage =
    storeDetails?.shippingType === ShippingType.HybridShipping &&
    storeAddressesData.length <= 0;

  const ftux = useFtux();
  const history = useHistory();

  const [ftuxDialogOpen, setFtuxDialogOpen] = useState(ftux);

  const handleClose = () => {
    setFtuxDialogOpen(false);
    history.replace({search: ''});
  };

  const showDialog = storeDetails && primaryCampaign && !isAddressesLoading;

  if (!showDialog) {
    return null;
  }

  return showHybridMessage ? (
    <HybridOrderDialog
      isOpen={ftuxDialogOpen}
      handleClose={handleClose}
      storeSlug={urlSlug}
      storeDetails={storeDetails}
      primaryCampaign={primaryCampaign}
    />
  ) : (
    <GroupOrderDialog
      isOpen={ftuxDialogOpen}
      handleClose={handleClose}
      storeSlug={urlSlug}
      storeDetails={storeDetails}
      primaryCampaign={primaryCampaign}
    />
  );
};

export default GroupFtuxDialog;
